'use client';

import React, { useState } from 'react';
import { foundationDocuments } from '@/data/kavrisData';
import StatusBadge from '@/components/ui/StatusBadge';
import AuthorityBadge from '@/components/ui/AuthorityBadge';
import DemoBadge from '@/components/ui/DemoBadge';
import Link from 'next/link';
import { ShieldCheck, BookOpen, Map, Layers, Building2, ArrowRight, ChevronDown,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const hierarchySteps = [
  {
    id: 'hier-foundation',
    label: 'KAVRIS Foundation',
    sublabel: 'Origin · Vision · Mission · Manifesto · Constitution · Charter · Product Identity',
    icon: ShieldCheck,
    color: 'text-[#22c55e]',
    borderColor: 'border-[#22c55e]/30',
    bgColor: 'bg-[#22c55e]/5',
  },
  {
    id: 'hier-codex-atlas',
    label: 'Codex + Atlas',
    sublabel: 'Authoritative knowledge · Governance · Architectural visualization',
    icon: BookOpen,
    color: 'text-primary',
    borderColor: 'border-primary/30',
    bgColor: 'bg-primary/5',
  },
  {
    id: 'hier-product-framework',
    label: 'Product Framework',
    sublabel: 'Structured architecture connecting KAVRIS concepts to products',
    icon: Layers,
    color: 'text-[#f59e0b]',
    borderColor: 'border-[#f59e0b]/30',
    bgColor: 'bg-[#f59e0b]/5',
  },
  {
    id: 'hier-pf001',
    label: 'PF-001 Enterprise Edition',
    sublabel: 'The first defined Product Framework implementation',
    icon: Building2,
    color: 'text-accent-foreground',
    borderColor: 'border-accent/30',
    bgColor: 'bg-accent/5',
  },
];

export default function ProductFrameworkWorkspace() {
  const [expandedSection, setExpandedSection] = useState<string | null>('overview');

  const toggleSection = (id: string) => {
    setExpandedSection(prev => (prev === id ? null : id));
  };

  return (
    <div className="min-h-full p-6 xl:p-8 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="text-xs font-mono-data text-muted-foreground mb-2">KAVRIS → Product Framework</div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">Product Framework</h1>
            <p className="text-sm text-muted-foreground max-w-xl">
              The structured architecture connecting KAVRIS foundational concepts to products and implementations.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status="Working" size="md" />
            <AuthorityBadge authority="Product Framework" size="md" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Left: Hierarchy diagram */}
        <div className="xl:col-span-2 space-y-8">
          {/* Architecture hierarchy */}
          <div className="p-6 rounded-xl border border-border bg-card card-elevation">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-1">
                  Architecture Hierarchy
                </div>
                <h2 className="text-base font-semibold text-foreground">KAVRIS Product Framework</h2>
              </div>
              <DemoBadge label="PROTOTYPE" />
            </div>

            <div className="flex flex-col items-center gap-0">
              {hierarchySteps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={step.id}>
                    <div
                      className={`w-full max-w-lg p-4 rounded-xl border ${step.borderColor} ${step.bgColor} transition-all duration-200`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center flex-shrink-0`}>
                          <Icon size={15} className={step.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-semibold ${step.color}`}>{step.label}</div>
                          <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{step.sublabel}</div>
                        </div>
                      </div>
                    </div>
                    {i < hierarchySteps.length - 1 && (
                      <div className="flex flex-col items-center my-1">
                        <div className="w-px h-4 bg-border" />
                        <ChevronDown size={12} className="text-muted-foreground" />
                        <div className="w-px h-4 bg-border" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* PF-001 Card */}
          <div className="p-6 rounded-xl border border-border bg-card card-elevation">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-1">
                  Currently Defined
                </div>
                <h2 className="text-base font-semibold text-foreground">Framework Items</h2>
              </div>
              <DemoBadge label="PROTOTYPE" />
            </div>

            {/* PF-001 */}
            <div className="p-5 rounded-xl border border-accent/25 bg-accent/5 hover:border-accent/40 transition-all duration-200">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col items-center justify-center w-12 h-12 rounded-lg border border-accent/30 bg-accent/10 flex-shrink-0">
                    <span className="text-[10px] font-mono-data text-accent-foreground font-bold">PF</span>
                    <span className="text-[10px] font-mono-data text-accent-foreground font-bold">001</span>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono-data text-muted-foreground tracking-widest uppercase">PF-001</div>
                    <div className="text-base font-semibold text-foreground">Enterprise Edition</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status="Draft" size="sm" />
                </div>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                The first defined item within the KAVRIS Product Framework. Enterprise Edition represents the
                enterprise-grade implementation of the KAVRIS ecosystem, governed by Codex and navigated via Atlas.
              </p>

              <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
                <div className="p-2.5 rounded border border-border bg-muted/20">
                  <div className="text-muted-foreground mb-0.5">Authority</div>
                  <div className="font-medium text-foreground">Product Framework</div>
                </div>
                <div className="p-2.5 rounded border border-border bg-muted/20">
                  <div className="text-muted-foreground mb-0.5">Version</div>
                  <div className="font-mono-data text-muted-foreground">v0.1-draft</div>
                </div>
                <div className="p-2.5 rounded border border-border bg-muted/20">
                  <div className="text-muted-foreground mb-0.5">Layer</div>
                  <div className="font-medium text-foreground">Enterprise</div>
                </div>
                <div className="p-2.5 rounded border border-border bg-muted/20">
                  <div className="text-muted-foreground mb-0.5">Parent</div>
                  <div className="font-medium text-foreground">Product Framework</div>
                </div>
              </div>

              <Link
                href="/pf-001-enterprise"
                className="group flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
              >
                Open PF-001 Enterprise workspace
                <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Placeholder for future PF items */}
            <div className="mt-4 p-4 rounded-xl border border-dashed border-border text-center">
              <p className="text-xs text-muted-foreground font-mono-data">
                Additional Product Framework items will be defined as the ecosystem evolves.
              </p>
              <p className="text-[10px] text-muted-foreground/60 mt-1 font-mono-data">
                No additional PF items are currently approved.
              </p>
            </div>
          </div>

          {/* Foundation documents reference */}
          <div className="p-6 rounded-xl border border-border bg-card card-elevation">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-1">
                  Foundation Reference
                </div>
                <h2 className="text-base font-semibold text-foreground">Executive Foundation Documents</h2>
              </div>
              <DemoBadge label="PLACEHOLDER" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {foundationDocuments.map(doc => (
                <div
                  key={`pf-found-${doc.id}`}
                  className="p-3 rounded-lg border border-border bg-muted/20 text-center"
                >
                  <div className="text-sm font-medium text-foreground mb-1">{doc.title}</div>
                  <StatusBadge status={doc.status} size="xs" />
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4 font-mono-data">
              Content reserved for approved KAVRIS source material.
            </p>
          </div>
        </div>

        {/* Right: Metadata + context */}
        <div className="space-y-5">
          {/* Framework metadata */}
          <div className="p-5 rounded-xl border border-border bg-card card-elevation">
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-4">
              Framework Metadata
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Status</span>
                <StatusBadge status="Working" size="sm" />
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Authority</span>
                <AuthorityBadge authority="Product Framework" size="sm" />
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Version</span>
                <span className="font-mono-data text-muted-foreground text-xs">v0.1</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Items defined</span>
                <span className="font-mono-data text-foreground text-xs">1 (PF-001)</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-muted-foreground">Layer</span>
                <span className="text-xs text-foreground">Product</span>
              </div>
            </div>
          </div>

          {/* Governing relationships */}
          <div className="p-5 rounded-xl border border-border bg-card card-elevation">
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-4">
              Governing Relationships
            </div>
            <div className="space-y-2">
              {[
                { label: 'Governed by', value: 'Codex', icon: BookOpen, href: '/codex' },
                { label: 'Mapped by', value: 'Atlas', icon: Map, href: '/atlas' },
                { label: 'Underpinned by', value: 'Foundation', icon: ShieldCheck, href: '/' },
              ].map(rel => {
                const Icon = rel.icon;
                return (
                  <Link
                    key={`pf-rel-${rel.label}`}
                    href={rel.href}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/30 transition-all text-sm group"
                  >
                    <Icon size={13} className="text-muted-foreground group-hover:text-primary transition-colors" />
                    <div className="flex-1">
                      <span className="text-muted-foreground text-xs">{rel.label}</span>
                      <div className="text-foreground font-medium text-xs mt-0.5">{rel.value}</div>
                    </div>
                    <ArrowRight size={12} className="text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                  </Link>
                );
              })}
            </div>
          </div>

          {/* KAVRIS Intelligence placeholder */}
          <div className="p-5 rounded-xl border border-[#a855f7]/20 bg-[#a855f7]/5 card-elevation">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-[#a855f7] animate-pulse" />
              <div className="text-[10px] font-mono-data tracking-widest uppercase text-[#a855f7]">
                KAVRIS Intelligence
              </div>
              <span className="ml-auto text-[9px] font-mono-data text-muted-foreground bg-muted/30 px-1.5 py-0.5 rounded">
                Prototype
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3">
              Future contextual intelligence layer. In a production system, this panel would surface
              governing principles, related architecture, and navigational context.
            </p>
            <div className="space-y-1.5">
              {[
                'Where does PF-001 belong in KAVRIS?',
                'Show related architecture.',
                'Navigate to governing principle.',
              ].map((q, qi) => (
                <div
                  key={`pf-q-${qi}`}
                  className="text-xs text-muted-foreground/70 px-2 py-1.5 rounded border border-border bg-muted/20 font-mono-data"
                >
                  {q}
                </div>
              ))}
            </div>
            <p className="text-[10px] font-mono-data text-muted-foreground/50 mt-3">
              Prototype interaction — mock data only
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}